import React from 'react';
import { Helmet } from 'react-helmet-async';

export default function CookiePolicyPage() {
  const lastUpdated = "12 Maggio 2025";

  return (
    <>
      <Helmet>
        <title>Cookie Policy | Rarifex</title>
        <meta name="description" content="Cookie Policy di Rarifex. Scopri quali cookie utilizziamo e come gestire le tue preferenze." />
        <link rel="canonical" href="https://www.rarifex.com/cookie" />
      </Helmet>

      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-4 py-12 md:py-16">
          <div className="max-w-4xl mx-auto">
            
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Cookie Policy
            </h1>
            <p className="text-slate-500 mb-8">
              Ultimo aggiornamento: {lastUpdated}
            </p>

            <div className="prose prose-slate max-w-none">
              
              <section className="mb-10">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">1. Cosa sono i Cookie</h2>
                <p className="text-slate-700 mb-4">
                  I cookie sono piccoli file di testo che i siti web visitati inviano al dispositivo dell'utente 
                  (computer, tablet, smartphone) e vengono memorizzati per essere ritrasmessi agli stessi siti 
                  alla visita successiva.
                </p>
                <p className="text-slate-700">
                  I cookie possono essere "di sessione" (cancellati alla chiusura del browser) o "persistenti" 
                  (memorizzati fino alla scadenza o cancellazione da parte dell'utente). Possono inoltre essere 
                  "di prima parte" (impostati dal sito visitato) o "di terze parti" (impostati da altri siti).
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">2. Titolare del Trattamento</h2>
                <p className="text-slate-700">
                  Il Titolare del trattamento è Rarifex S.r.l. Per contatti: 
                  <a href="mailto:privacy@rarifex.com" className="text-shop-primary hover:underline"> privacy@rarifex.com</a>
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">3. Tipologie di Cookie Utilizzati</h2>
                
                <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">3.1 Cookie Tecnici (necessari)</h3>
                <p className="text-slate-700 mb-4">
                  Questi cookie sono essenziali per il funzionamento della Piattaforma e non possono essere disabilitati. 
                  Includono:
                </p>
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-slate-200 mb-4">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-slate-700 border-b">Cookie</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-slate-700 border-b">Finalità</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-slate-700 border-b">Durata</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-700 text-sm">
                      <tr>
                        <td className="px-4 py-2 border-b">session_id</td>
                        <td className="px-4 py-2 border-b">Gestione della sessione utente</td>
                        <td className="px-4 py-2 border-b">Sessione</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b">auth_token</td>
                        <td className="px-4 py-2 border-b">Autenticazione utente</td>
                        <td className="px-4 py-2 border-b">30 giorni</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b">csrf_token</td>
                        <td className="px-4 py-2 border-b">Sicurezza (protezione CSRF)</td>
                        <td className="px-4 py-2 border-b">Sessione</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b">cart_id</td>
                        <td className="px-4 py-2 border-b">Gestione carrello acquisti</td>
                        <td className="px-4 py-2 border-b">7 giorni</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b">cookie_consent</td>
                        <td className="px-4 py-2 border-b">Memorizzazione preferenze cookie</td>
                        <td className="px-4 py-2 border-b">12 mesi</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-slate-700">
                  <strong>Base giuridica:</strong> Legittimo interesse (art. 6(1)(f) GDPR) - necessari per il 
                  funzionamento del servizio.
                </p>

                <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">3.2 Cookie Analitici</h3>
                <p className="text-slate-700 mb-4">
                  Questi cookie ci permettono di raccogliere informazioni aggregate sull'utilizzo della Piattaforma 
                  per migliorare i nostri servizi.
                </p>
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-slate-200 mb-4">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-slate-700 border-b">Cookie</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-slate-700 border-b">Provider</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-slate-700 border-b">Finalità</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-slate-700 border-b">Durata</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-700 text-sm">
                      <tr>
                        <td className="px-4 py-2 border-b">_ga, _gid</td>
                        <td className="px-4 py-2 border-b">Google Analytics</td>
                        <td className="px-4 py-2 border-b">Statistiche di utilizzo</td>
                        <td className="px-4 py-2 border-b">2 anni / 24 ore</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-slate-700">
                  <strong>Base giuridica:</strong> Consenso dell'utente (art. 6(1)(a) GDPR).
                </p>

                <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">3.3 Cookie di Terze Parti</h3>
                <p className="text-slate-700 mb-4">
                  La Piattaforma utilizza servizi di terze parti che possono impostare propri cookie:
                </p>
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-slate-200 mb-4">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-slate-700 border-b">Servizio</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-slate-700 border-b">Finalità</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-slate-700 border-b">Privacy Policy</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-700 text-sm">
                      <tr>
                        <td className="px-4 py-2 border-b">Stripe</td>
                        <td className="px-4 py-2 border-b">Elaborazione pagamenti</td>
                        <td className="px-4 py-2 border-b">
                          <a href="https://stripe.com/it/privacy" target="_blank" rel="noopener noreferrer" className="text-shop-primary hover:underline">Link</a>
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b">Google</td>
                        <td className="px-4 py-2 border-b">Autenticazione, Analytics</td>
                        <td className="px-4 py-2 border-b">
                          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-shop-primary hover:underline">Link</a>
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b">Supabase</td>
                        <td className="px-4 py-2 border-b">Backend e autenticazione</td>
                        <td className="px-4 py-2 border-b">
                          <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer" className="text-shop-primary hover:underline">Link</a>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">4. Gestione delle Preferenze</h2>
                <p className="text-slate-700 mb-4">
                  L'utente può gestire le proprie preferenze sui cookie in diversi modi:
                </p>
                
                <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">4.1 Banner Cookie</h3>
                <p className="text-slate-700 mb-4">
                  Al primo accesso alla Piattaforma, viene mostrato un banner che permette di accettare o rifiutare 
                  i cookie non essenziali. Le preferenze possono essere modificate in qualsiasi momento.
                </p>

                <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">4.2 Impostazioni del Browser</h3>
                <p className="text-slate-700 mb-4">
                  È possibile gestire i cookie attraverso le impostazioni del proprio browser. Di seguito i link 
                  alle guide dei principali browser:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>
                    <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-shop-primary hover:underline">Google Chrome</a>
                  </li>
                  <li>
                    <a href="https://support.mozilla.org/it/kb/protezione-antitracciamento-avanzata-firefox-desktop" target="_blank" rel="noopener noreferrer" className="text-shop-primary hover:underline">Mozilla Firefox</a>
                  </li>
                  <li>
                    <a href="https://support.apple.com/it-it/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-shop-primary hover:underline">Safari</a>
                  </li>
                  <li>
                    <a href="https://support.microsoft.com/it-it/microsoft-edge/eliminare-i-cookie-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-shop-primary hover:underline">Microsoft Edge</a>
                  </li>
                </ul>

                <h3 className="text-lg font-medium text-slate-800 mt-6 mb-3">4.3 Opt-out Specifici</h3>
                <p className="text-slate-700 mb-4">
                  Per disattivare specifici cookie di terze parti:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>
                    Google Analytics: <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-shop-primary hover:underline">https://tools.google.com/dlpage/gaoptout</a>
                  </li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">5. Conseguenze della Disabilitazione</h2>
                <p className="text-slate-700">
                  La disabilitazione dei cookie tecnici potrebbe compromettere il funzionamento della Piattaforma. 
                  La disabilitazione dei cookie analitici non influisce sulla navigazione ma ci impedisce di 
                  raccogliere dati per migliorare i servizi.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">6. Diritti dell'Utente</h2>
                <p className="text-slate-700 mb-4">
                  In relazione ai dati raccolti tramite cookie, l'utente può esercitare i diritti previsti dal GDPR 
                  (accesso, rettifica, cancellazione, limitazione, portabilità, opposizione) contattando: 
                  <a href="mailto:privacy@rarifex.com" className="text-shop-primary hover:underline"> privacy@rarifex.com</a>
                </p>
                <p className="text-slate-700">
                  Per maggiori informazioni, consultare la nostra 
                  <a href="/privacy" className="text-shop-primary hover:underline"> Informativa sulla Privacy</a>.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">7. Aggiornamenti</h2>
                <p className="text-slate-700">
                  La presente Cookie Policy può essere aggiornata periodicamente. La data dell'ultimo aggiornamento 
                  è indicata in cima a questa pagina. Si consiglia di consultare periodicamente questa pagina.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">8. Contatti</h2>
                <p className="text-slate-700">
                  Per qualsiasi domanda relativa all'uso dei cookie:
                </p>
                <ul className="list-none pl-0 text-slate-700 mt-4 space-y-1">
                  <li><strong>Email:</strong> <a href="mailto:privacy@rarifex.com" className="text-shop-primary hover:underline">privacy@rarifex.com</a></li>
                </ul>
              </section>

            </div>
          </div>
        </div>
      </div>
    </>
  );
}
